(() => {
  'use strict';
  const renderers = new WeakMap(), jobs = new WeakMap();
  const pi = Math.PI;
  function makeRenderer(){return{surface:document.createElement('canvas'),ratio:0};}
  function geometry(renderer, ratio) {
    const cw=1440, fw=1230, fh=fw*ratio, ox=105, oy=85, ch=Math.ceil(fh+215);
    const zAt=(u,v)=>{
      const free=.38+.62*Math.sin(Math.min(1,v*1.25)*pi/2);
      const broad=18*Math.sin(2*pi*(u*2.15+.15*Math.sin(v*3.4)))+11*Math.sin(2*pi*(u*3.65-.17*v+.12));
      const crease=(centre,width,amount)=>amount*Math.exp(-Math.pow((u-centre)/width,2));
      const folds=crease(.19+.12*v,.027,-13)+crease(.52-.12*v,.04,16)+crease(.83-.10*v,.024,-10);
      let tension=0;
      for(const side of [0,1]){
        const x=(side?1-u:u)*fw,y=v*fh,r=Math.hypot(x,y),angle=Math.atan2(y,x);
        tension+=7*Math.sin(angle*15+r*.004)*Math.exp(-r/340)*Math.min(1,r/65);
      }
      const detail=1.25*Math.sin(u*48+v*27)*Math.sin(v*11+u*9);
      const pin=Math.min(1,Math.hypot(u*fw,v*fh)/85,Math.hypot((1-u)*fw,v*fh)/85);
      return ((broad+folds)*free+tension+detail)*pin;
    };
    const point=(u,v)=>{
      const z=zAt(u,v), sag=Math.sin(pi*u);
      return [ox+u*fw+10*Math.sin(pi*v)*Math.sin(u*4.8)-z*.20,
        oy+v*fh+sag*(19+24*v)+Math.sin(u*7.9+1.1)*v*v*11-z*.26,z];
    };
    const cols=160,rows=Math.max(70,Math.round(120*ratio)),verts=[],indices=[];
    for(let j=0;j<=rows;j++)for(let i=0;i<=cols;i++){
      const u=i/cols,v=j/rows,[x,y,z]=point(u,v),e=.0006;
      const nx=-(zAt(u+e,v)-zAt(u-e,v))/(2*e*fw),ny=-(zAt(u,v+e)-zAt(u,v-e))/(2*e*fh);
      verts.push(x/cw*2-1,1-y/ch*2,u,v,nx,ny,1,z);
    }
    for(let j=0;j<rows;j++)for(let i=0;i<cols;i++){
      const a=j*(cols+1)+i,b=a+1,c=a+cols+1,d=c+1;indices.push(a,b,c,b,d,c);
    }
    const {surface}=renderer;surface.width=cw;surface.height=ch;
    const samples=new Float32Array(cw*ch*3),light=[-.446,-.567,.688];
    const vertices=[];
    for(let i=0;i<verts.length;i+=8)vertices.push([(verts[i]+1)*cw/2,(1-verts[i+1])*ch/2,...verts.slice(i+2,i+8)]);
    for(let i=0;i<indices.length;i+=3){
      const a=vertices[indices[i]],b=vertices[indices[i+1]],c=vertices[indices[i+2]];
      const area=(b[1]-c[1])*(a[0]-c[0])+(c[0]-b[0])*(a[1]-c[1]);
      const x0=Math.max(0,Math.floor(Math.min(a[0],b[0],c[0]))),x1=Math.min(cw-1,Math.ceil(Math.max(a[0],b[0],c[0])));
      const y0=Math.max(0,Math.floor(Math.min(a[1],b[1],c[1]))),y1=Math.min(ch-1,Math.ceil(Math.max(a[1],b[1],c[1])));
      for(let y=y0;y<=y1;y++)for(let x=x0;x<=x1;x++){
        const wa=((b[1]-c[1])*(x+.5-c[0])+(c[0]-b[0])*(y+.5-c[1]))/area;
        const wb=((c[1]-a[1])*(x+.5-c[0])+(a[0]-c[0])*(y+.5-c[1]))/area,wc=1-wa-wb;
        if(wa<-.00001||wb<-.00001||wc<-.00001)continue;
        const k=(y*cw+x)*3,u=wa*a[2]+wb*b[2]+wc*c[2],v=wa*a[3]+wb*b[3]+wc*c[3];
        const nx=wa*a[4]+wb*b[4]+wc*c[4],ny=wa*a[5]+wb*b[5]+wc*c[5],z=wa*a[7]+wb*b[7]+wc*c[7];
        const diffuse=Math.max(0,(nx*light[0]+ny*light[1]+light[2])/Math.hypot(nx,ny,1));
        const valley=1-Math.max(0,Math.min(1,(-z-4)/24))*.07;
        const weave=Math.sin(u*fw*pi)*Math.sin(v*fh*pi)*.012;
        samples[k]=u;samples[k+1]=v;samples[k+2]=Math.pow((.52+.6*diffuse)*valley,1/2.2)*(1+weave);
      }
    }
    Object.assign(renderer,{ratio,cw,ch,fw,fh,point,samples});
  }
  function paintFabric(renderer,texture){
    const ctx=renderer.surface.getContext('2d'),pixels=ctx.createImageData(renderer.cw,renderer.ch),dest=pixels.data;
    const tw=texture.width,th=texture.height,source=texture.getContext('2d').getImageData(0,0,tw,th).data;
    const {samples}=renderer;
    for(let i=0,j=0;i<dest.length;i+=4,j+=3){
      if(!samples[j+2])continue;
      const x=Math.max(0,Math.min(tw-1.001,samples[j]*(tw-1))),y=Math.max(0,Math.min(th-1.001,samples[j+1]*(th-1)));
      const ix=Math.floor(x),iy=Math.floor(y),fx=x-ix,fy=y-iy,a=(iy*tw+ix)*4,b=a+4,c=a+tw*4,d=c+4;
      const grain=(((i*1664525+1013904223)>>>0)%256/255-.5)*.75,shade=samples[j+2];
      for(let k=0;k<3;k++)dest[i+k]=Math.min(255,((source[a+k]*(1-fx)+source[b+k]*fx)*(1-fy)+(source[c+k]*(1-fx)+source[d+k]*fx)*fy)*shade+grain);
      dest[i+3]=255;
    }
    ctx.putImageData(pixels,0,0);
  }

  function boundary(ctx,point,inset=0){
    const edge=80;ctx.beginPath();
    for(let i=0;i<=edge;i++){const p=point(inset+(1-2*inset)*i/edge,inset);i?ctx.lineTo(p[0],p[1]):ctx.moveTo(p[0],p[1]);}
    for(let i=1;i<=edge;i++){const p=point(1-inset,inset+(1-2*inset)*i/edge);ctx.lineTo(p[0],p[1]);}
    for(let i=edge-1;i>=0;i--){const p=point(inset+(1-2*inset)*i/edge,1-inset);ctx.lineTo(p[0],p[1]);}
    for(let i=edge-1;i>0;i--){const p=point(inset,inset+(1-2*inset)*i/edge);ctx.lineTo(p[0],p[1]);}ctx.closePath();
  }
  function eyelet(ctx,point,u,v,top){
    const [x,y]=point(u,v),r=6.8;
    ctx.save();ctx.translate(x,y);ctx.rotate(-.05+u*.08);
    ctx.shadowColor='#00000040';ctx.shadowBlur=2;ctx.shadowOffsetY=1;
    const metal=ctx.createLinearGradient(-r,-r,r,r);
    metal.addColorStop(0,'#fafafa');metal.addColorStop(.2,'#a2a3a0');metal.addColorStop(.42,'#f4f5f2');metal.addColorStop(.7,'#737773');metal.addColorStop(1,'#d5d8d2');
    ctx.beginPath();ctx.ellipse(0,0,r,r*.93,0,0,2*pi);ctx.fillStyle=metal;ctx.fill();ctx.shadowColor='transparent';
    ctx.strokeStyle='#4a514968';ctx.lineWidth=.8;ctx.stroke();
    ctx.beginPath();ctx.ellipse(0,0,3.5,3.2,0,0,2*pi);ctx.fillStyle='#a3a59b';ctx.fill();ctx.strokeStyle='#555951';ctx.lineWidth=1;ctx.stroke();
    ctx.beginPath();ctx.arc(-.5,-.7,4.4,pi*1.02,pi*1.75);ctx.strokeStyle='#ffffffc0';ctx.lineWidth=.7;ctx.stroke();
    if(top){ctx.beginPath();ctx.arc(-.7,-.5,1.8,0,pi*2);ctx.fillStyle='#747a73';ctx.fill();ctx.fillStyle='#e6e6e1';ctx.fillRect(-1.3,-1.5,1.8,.6);}
    ctx.restore();
  }
  window.ffRenderFlag=async(svg,canvas)=>{
    const started=performance.now(),token={};jobs.set(canvas,token);
    let renderer=renderers.get(canvas);
    if(!renderer){renderer=makeRenderer();renderers.set(canvas,renderer);}
    const clone=svg.cloneNode(true);clone.removeAttribute('hidden');clone.removeAttribute('style');clone.querySelectorAll('[data-guides]').forEach(e=>e.remove());
    const [,,width,height]=clone.getAttribute('viewBox').split(/\s+/).map(Number),ratio=height/width;
    clone.setAttribute('xmlns','http://www.w3.org/2000/svg');clone.setAttribute('width','1800');clone.setAttribute('height',String(Math.round(1800*ratio)));
    const url=URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)],{type:'image/svg+xml'}));
    try{
      const im=await new Promise((resolve,reject)=>{const image=new Image;image.onload=()=>resolve(image);image.onerror=reject;image.src=url;});
      if(jobs.get(canvas)!==token)return false;
      if(renderer.ratio!==ratio)geometry(renderer,ratio);
      const {cw,ch,point}=renderer,texture=document.createElement('canvas');texture.width=1800;texture.height=Math.round(1800*ratio);
      const tx=texture.getContext('2d');tx.drawImage(im,0,0,texture.width,texture.height);
      tx.strokeStyle='#ffffff1c';tx.lineWidth=14;tx.strokeRect(8,8,texture.width-16,texture.height-16);
      tx.strokeStyle='#22282038';tx.lineWidth=1.3;tx.strokeRect(17,17,texture.width-34,texture.height-34);
      tx.setLineDash([3,2]);tx.lineWidth=1;tx.strokeStyle='#ffffffba';tx.strokeRect(14,14,texture.width-28,texture.height-28);
      tx.strokeStyle='#ffffff70';tx.strokeRect(19,19,texture.width-38,texture.height-38);
      paintFabric(renderer,texture);
      canvas.width=cw;canvas.height=ch;const ctx=canvas.getContext('2d');
      const wall=ctx.createRadialGradient(cw*.24,ch*.12,30,cw*.5,ch*.5,cw*.8);wall.addColorStop(0,'#f2f0ea');wall.addColorStop(.65,'#e5e3dd');wall.addColorStop(1,'#d5d5ce');ctx.fillStyle=wall;ctx.fillRect(0,0,cw,ch);
      ctx.save();ctx.translate(6,10);ctx.filter='blur(10px)';ctx.shadowOffsetX=9;ctx.shadowOffsetY=17;ctx.shadowBlur=27;ctx.shadowColor='#292c2550';ctx.fillStyle='#55584d';boundary(ctx,point);ctx.fill();ctx.restore();
      ctx.save();ctx.shadowOffsetY=4;ctx.shadowBlur=6;ctx.shadowColor='#32382a35';ctx.fillStyle='#fff';boundary(ctx,point);ctx.fill();ctx.restore();
      ctx.drawImage(renderer.surface,0,0);
      for(const [u,v,top]of [[.014,.022,true],[.986,.022,true],[.014,.978,false],[.986,.978,false]])eyelet(ctx,point,u,v,top);
      canvas.dataset.renderer='cloth-mesh';canvas.dataset.renderMs=String(Math.round(performance.now()-started));
      return true;
    }finally{URL.revokeObjectURL(url);}
  };
})();
